<template>
  <v-container fluid>
    <Loading :loading="!loaded" />

    <div v-if="loaded">
      <v-row>
        <v-col>
          <!-- ---- main card ---- -->
          <v-card
            elevation="4"
            outlined
          >
            <v-toolbar flat>
              <v-toolbar-title>
                {{ $t('account.title') }}
              </v-toolbar-title>

              <v-spacer />

              <div class="d-flex justify-space-between" style="gap: 1rem;">
                <v-btn
                  outlined
                  color="primary"
                  @click="cancel"
                >
                  {{ $t('common.cancel') }}
                </v-btn>
                <v-btn
                  color="secondary"
                  :loading="saving"
                  @click="save"
                >
                  {{ $t('common.save') }}
                </v-btn>
              </div>
            </v-toolbar>
            <v-card-text>
              <v-container
                v-if="model"
                fluid
              >
                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="model.data.firstName"
                      :label="t('account.firstNameLabel')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="model.data.office"
                      :label="t('account.officeLabel')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="model.data.lastName"
                      :label="t('account.lastNameLabel')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="model.data.title"
                      :label="t('account.titleLabel')"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="model.data.email"
                      outlined
                      dense
                      :label="t('account.emailLabel')"
                    />
                  </v-col>
                  <v-col>
                    <div style="margin-top: -34px;">
                      <label class="v-text-field v-input v-label text-caption">
                        {{ $t('account.telePhoneLabel') }}
                      </label>
                      <vue-tel-input
                        v-bind="bindTelInputPhoneProps"
                        ref="phone"
                        v-model="model.data.phone"
                      />
                    </div>
                  </v-col>
                  <v-col>
                    <div style="margin-top: -34px;">
                      <label class="v-text-field v-input v-label text-caption">
                        {{ $t('account.cellPhoneLabel') }}
                      </label>
                      <vue-tel-input
                        v-bind="bindTelInputCellPhoneProps"
                        ref="phone"
                        v-model="model.data.cellPhone"
                      />
                    </div>
                  </v-col>
                  <v-col>
                    <v-select
                      v-model="model.data.preferredLocale"
                      :items="[ ...$store.state.availableLocales, { text: $t('account.useDefaultLocale'), value: null } ]"
                      :label="$t('account.preferredLocale')"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>

                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <h3>{{ $t('account.notificationPreferencesTitle') }}</h3>
                    <span class="caption">
                      {{ $tc('account.notificationPreferencesSubtitle') }}
                    </span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-simple-table dense>
                      <template #default>
                        <tbody>
                          <tr
                            v-for="(np,i) in model.data.notificationPreferences"
                            :key="i"
                          >
                            <th>{{ np.text }}</th>
                            <td>
                              <v-checkbox
                                v-model="np.sendMail"
                                :label="t('account.checkboxSendEmail')"
                              />
                            </td>
                            <td>
                              <v-checkbox
                                v-model="np.sendSMS"
                                :label="t('account.checkboxSendSMS')"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>

import { VueTelInput } from 'vue-tel-input'
import Loading from '@/components/Loading'
import { setI18nLanguage } from '@/services/i18n-setup';

var searchTimeout = null;
  
export default {  
  name: 'Account',
  components: {
    VueTelInput,
    Loading
  },
  data() {
      return {
      model: null,
      //view only
      loaded: false,
      saving: false,
      saveSuccess: false,
      t : this.$t,
      bindTelInputPhoneProps: {
        //https://educationlink.github.io/vue-tel-input/documentation/props.html
        mode: "international", //Format number to 'international' (with + dial code) or 'national' (with 0...)
        defaultCountry: "dk", //Default country, will override the country fetched from IP address of user
        autoDefaultCountry: false,
        validCharactersOnly: true, //only numbers
        preferredCountries: ['dk'],
        name: "telephone",
        inputOptions: {
            name: "telephoneinput",
            placeholder: this.$t('account.telePhoneLabel')
        }
      },
      bindTelInputCellPhoneProps: {
        //https://educationlink.github.io/vue-tel-input/documentation/props.html
        mode: "international", //Format number to 'international' (with + dial code) or 'national' (with 0...)
        defaultCountry: "dk", //Default country, will override the country fetched from IP address of user
        autoDefaultCountry: false,
        validCharactersOnly: true, //only numbers
        preferredCountries: ['dk'],
        name: "cellphone",
        inputOptions: {
            name: "cellphoneinput",
            placeholder: this.$t('account.cellPhoneLabel')
        }
      },      
    }
  },
  created() {
    this.init();
  },
  methods: {
    init: async function() {
      this.$api.get(`users/edit`).then(response => {
        this.model = response.data;
        this.loaded = true;
      });
    },
    save: async function() {
      this.saving = true;
      
      try {
        const { data } = await this.$api.put(`users/edit`, this.model.data)

        if (!data.success) throw 'Error saving account';

        await this.$store.dispatch('loadUser')
        await this.init();
        this.$notifier.showSuccess({ message: this.$t('common.saveSuccess') })
      } catch (error) {
        this.$notifier.showError({});
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    cancel: function() {
      this.init();
    }
  }
}
</script>
